import {
  MdCheckCircle,
  MdOutlineAttachMoney,
  MdCalendarToday,
} from "react-icons/md";
import Card from "components/card";
import { useNavigate } from "react-router-dom";
import { cancelSubscriptionPlan } from 'services/apiService';
import { useState } from 'react';

export interface SubscriptionPlan {
  name: string;
  amount_in_usd: string;
  interval: string;
  descriptions?: Array<{ point: string }>;
}

interface SubscriptionCardProps {
  subscriptionPlan?: SubscriptionPlan | null;
}

const SubscriptionCard: React.FC<SubscriptionCardProps> = ({ subscriptionPlan }) => {
  const navigate = useNavigate();
  const [isCancelling, setIsCancelling] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelledUntil, setCancelledUntil] = useState<string | null>(null);

  const handleUpgradeClick = () => {
    navigate("/dashboard/profile?tab=pricing");
  };

  const handleCancelClick = async () => {
    setIsCancelling(true);
    try {
      const data = await cancelSubscriptionPlan();
      if (data.access_until) {
        const date = new Date(data.access_until).toLocaleDateString('en-US', {
          year: 'numeric', month: 'long', day: 'numeric',
        });
        setCancelledUntil(date);
      } else {
        navigate("/dashboard/pricing");
      }
    } catch (error) {
      console.error("Error cancelling subscription:", error);
    } finally {
      setIsCancelling(false);
      setShowCancelConfirm(false);
    }
  };

  if (cancelledUntil) {
    return (
      <Card extra="!p-[20px]">
        <div className="flex flex-col items-center text-center gap-4 py-4">
          <div className="h-12 w-12 rounded-full bg-amber-100 flex items-center justify-center">
            <MdCalendarToday className="h-6 w-6 text-amber-600" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-[#1F2937] dark:text-white">Subscription Cancelled</h3>
            <p className="text-sm text-[#6B7280] mt-1">Your subscription has been cancelled.</p>
          </div>
          <div className="w-full rounded-lg bg-amber-50 border border-amber-200 px-4 py-3">
            <p className="text-sm font-medium text-amber-800">
              You have access to your dashboard until
            </p>
            <p className="text-base font-bold text-amber-900 mt-0.5">{cancelledUntil}</p>
          </div>
          <p className="text-xs text-[#6B7280]">After this date, your dashboard access will be revoked. You can re-subscribe at any time.</p>
          <button
            onClick={() => navigate("/dashboard/pricing")}
            className="w-full rounded-lg bg-brand-500 py-2.5 text-sm font-semibold text-white hover:bg-brand-600 transition-colors"
          >
            View Plans
          </button>
        </div>
      </Card>
    );
  }

  return (
    <Card extra="!p-[20px] text-center">
      {/* Check if there is an active subscription plan */}
      {subscriptionPlan ? (
        <>
          {/* Header with Pricing Plan Details */}
          <div className="flex justify-between">
            <div className="text-left">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-xl font-semibold text-[#1F2937] dark:text-white">{subscriptionPlan.name}</h3>
                <span className="inline-flex items-center gap-1 rounded-full bg-brand-100 px-2.5 py-0.5 text-xs font-semibold text-brand-600">
                  <MdCheckCircle className="h-3.5 w-3.5" /> Active
                </span>
              </div>
            </div>
            <button className="!linear z-[1] flex items-center justify-center rounded-lg bg-lightPrimary p-2 text-brand-500 !transition !duration-200 hover:bg-gray-100 active:bg-gray-200 dark:bg-navy-700 dark:text-white dark:hover:bg-white/20 dark:active:bg-white/10">
              <MdOutlineAttachMoney className="h-6 w-6" />
            </button>
          </div>

          {/* Pricing Information */}
          <div className="mt-[20px]">
            <p className="text-4xl font-bold text-[#1F2937] dark:text-white">
              ${subscriptionPlan.amount_in_usd}
              <span className="text-sm font-normal text-[#6B7280] dark:text-gray-300">/{subscriptionPlan.interval}</span>
            </p>
          </div>

          {/* Features List */}
          <div className="mt-[20px] text-left">
            {subscriptionPlan?.descriptions?.map((description, i) => (
              <div key={i} className="flex items-center gap-2">
                <MdCheckCircle className="text-brand-500 shrink-0" />
                <p className="text-sm text-[#6B7280] dark:text-gray-300">{description.point}</p>
              </div>
            ))}
          </div>

          {/* Upgrade Button */}
          <button
            className="mt-6 w-full rounded-lg bg-brand-500 py-2 text-white transition duration-200 hover:bg-brand-600 active:bg-brand-700"
            onClick={handleUpgradeClick}
          >
            Upgrade Now
          </button>

          {/* Cancel flow */}
          {showCancelConfirm ? (
            <div className="mt-6 rounded-lg border border-red-200 bg-red-50 p-4 text-left dark:border-red-800 dark:bg-red-900/20">
              <p className="text-sm font-medium text-red-700 dark:text-red-400">Cancel your subscription?</p>
              <p className="mt-1 text-xs text-red-600 dark:text-red-500">You'll keep access until the end of the current billing period.</p>
              <div className="mt-3 flex gap-2">
                <button
                  onClick={handleCancelClick}
                  disabled={isCancelling}
                  className="flex-1 rounded-lg bg-red-500 py-2 text-sm font-semibold text-white transition duration-200 hover:bg-red-600 disabled:opacity-60"
                >
                  {isCancelling ? 'Cancelling…' : 'Yes, cancel'}
                </button>
                <button
                  onClick={() => setShowCancelConfirm(false)}
                  disabled={isCancelling}
                  className="flex-1 rounded-lg border border-gray-300 py-2 text-sm font-semibold text-gray-700 transition duration-200 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-white/5"
                >
                  Keep plan
                </button>
              </div>
            </div>
          ) : (
            <button
              className="mt-6 w-full rounded-lg border border-red-300 py-2 text-sm font-semibold text-red-600 transition duration-200 hover:bg-red-50 dark:border-red-700 dark:text-red-400 dark:hover:bg-red-900/20"
              onClick={() => setShowCancelConfirm(true)}
            >
              Cancel Subscription
            </button>
          )}
        </>
      ) : (
        /* No active plan message */
        <div className="mt-[20px]">
          <h3 className="text-2xl font-bold text-navy-700 dark:text-white">No active plan for your organization</h3>
          <p className="text-sm text-gray-600 dark:text-gray-300 mt-4">Please contact support or choose a plan to get started.</p>
        </div>
      )}
    </Card>
  );
};

export default SubscriptionCard;
